import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay } from 'rxjs';
import { BaseMasterDataService } from 'src/app/services/base-master-data.service';

export interface WorkforceFinanceCurrencyOption {
  label: string;
  value: string;
}

/**
 * Workforce Finance uses the shared country master endpoint as its only
 * currency source. One option is exposed per currency code, even where that
 * currency is used by more than one country.
 */
@Injectable({ providedIn: 'root' })
export class WorkforceFinanceCurrencyService {
  readonly currencyOptions$: Observable<WorkforceFinanceCurrencyOption[]>;

  constructor(private masterData: BaseMasterDataService) {
    this.currencyOptions$ = this.masterData.getCountries().pipe(
      map(countries => {
        const currencies = new Map<string, WorkforceFinanceCurrencyOption>();
        (countries || []).forEach(country => {
          const code = country.currencyCode?.trim().toUpperCase();
          if (!code || currencies.has(code)) return;
          const description = country.currencyLong?.trim() || country.name?.trim() || code;
          currencies.set(code, { value: code, label: `${code} — ${description}` });
        });
        return [...currencies.values()].sort((left, right) => left.label.localeCompare(right.label));
      }),
      catchError(() => of([] as WorkforceFinanceCurrencyOption[])),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }
}
